import { useCallback, useEffect, useState } from "react";
import { api } from "../api.js";
import {
  Badge, Button, EmptyState, Field, LoadingRows, Notice, PageHeader, Panel,
  SelectField, TextAreaField, cn,
} from "../components/ui.jsx";

const CATEGORIES = [
  ["business", "Business"],
  ["goals", "Goals"],
  ["messaging", "Messaging"],
  ["process", "Process"],
  ["reference", "Reference"],
  ["notes", "Notes"],
];

function newDocument() {
  return {
    id: null,
    title: "",
    category: "business",
    content: "",
    include_in_agent_context: true,
    revision: null,
    lifecycle: "active",
  };
}

function dateLabel(value) {
  if (!value) return "Not saved";
  return new Intl.DateTimeFormat("en", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

export default function AgentContextPage() {
  const [state, setState] = useState({ loading: true, documents: [], error: "" });
  const [document, setDocument] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const loadDocument = useCallback(async (id) => {
    const data = await api.workspaceDocument(id);
    setDocument(data.workspace_document);
    setSaved(false);
  }, []);

  const load = useCallback(async (preferredId = null) => {
    try {
      const data = await api.workspaceDocuments({ lifecycle: "active" });
      const documents = data.workspace_documents;
      setState({ loading: false, documents, error: "" });
      const nextId = preferredId || document?.id || documents[0]?.id;
      if (nextId && documents.some((item) => item.id === nextId)) await loadDocument(nextId);
      else if (!documents.length) setDocument(null);
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: error.message }));
    }
  }, [document?.id, loadDocument]);

  useEffect(() => {
    api.workspaceDocuments({ lifecycle: "active" })
      .then(async (data) => {
        setState({ loading: false, documents: data.workspace_documents, error: "" });
        if (data.workspace_documents[0]) await loadDocument(data.workspace_documents[0].id);
      })
      .catch((error) => setState({ loading: false, documents: [], error: error.message }));
  }, [loadDocument]);

  function update(key, value) {
    setSaved(false);
    setDocument((current) => ({ ...current, [key]: value }));
  }

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setSaved(false);
    setState((current) => ({ ...current, error: "" }));
    try {
      const input = {
        title: document.title,
        category: document.category,
        content: document.content,
        include_in_agent_context: document.include_in_agent_context,
      };
      const data = document.id
        ? await api.updateWorkspaceDocument(document.id, { ...input, expected_revision: document.revision })
        : await api.createWorkspaceDocument(input);
      const savedDocument = data.workspace_document;
      setDocument(savedDocument);
      setSaved(true);
      await load(savedDocument.id);
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
    } finally {
      setBusy(false);
    }
  }

  async function select(id) {
    setState((current) => ({ ...current, error: "" }));
    try { await loadDocument(id); } catch (error) { setState((current) => ({ ...current, error: error.message })); }
  }

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="Shared memory"
        title="Agent instructions and context"
        description="Define the business information and workspace instructions authorized agents should use when they plan and act."
        action={<Button onClick={() => { setDocument(newDocument()); setSaved(false); }}>New document</Button>}
      />
      {state.error && <Notice>{state.error}</Notice>}
      <Notice tone="info">Included documents are active workspace instructions and business context. When a user explicitly asks an agent to remember durable workspace-wide information, the agent can save it here. Person- or company-specific facts belong in CRM memory, and casual or temporary conversation should not be saved. Higher-priority instructions, security controls, and permissions still apply. Every edit creates a new revision.</Notice>

      {state.loading ? <LoadingRows count={5} /> : (
        <div className="grid items-start gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside className="min-w-0 overflow-hidden rounded-xl bg-card shadow-soft" aria-label="Workspace context documents">
            <div className="border-b border-neutral-100 p-4">
              <p className="text-base font-semibold text-foreground sm:text-sm">Memory files</p>
              <p className="text-base text-muted-foreground sm:text-sm">{state.documents.length} active</p>
            </div>
            {!state.documents.length ? (
              <div className="p-4 text-base text-muted-foreground sm:text-sm">Create the first document to give agents durable business context.</div>
            ) : (
              <div className="grid max-h-[560px] overflow-y-auto" role="list">
                {state.documents.map((item) => (
                  <button
                    className={cn(
                      "grid min-w-0 cursor-pointer gap-2 border-b border-neutral-100 bg-transparent p-4 text-left transition-colors last:border-b-0 hover:bg-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary",
                      document?.id === item.id && "bg-accent",
                    )}
                    key={item.id}
                    type="button"
                    role="listitem"
                    onClick={() => select(item.id)}
                  >
                    <span className="flex min-w-0 items-center justify-between gap-3">
                      <span className="truncate font-semibold text-foreground">{item.title}</span>
                      <Badge>{item.category}</Badge>
                    </span>
                    <span className="line-clamp-2 text-base text-muted-foreground sm:text-sm">{item.preview || "Empty document"}</span>
                    <span className="text-xs tabular-nums text-muted-foreground">Revision {item.revision}</span>
                  </button>
                ))}
              </div>
            )}
          </aside>

          {!document ? (
            <Panel><EmptyState title="Select or create a memory file" description="Use separate documents for business information, goals, messaging, processes, and reference material." /></Panel>
          ) : (
            <Panel>
              <form className="grid gap-6" onSubmit={save}>
                <div className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_220px]">
                  <Field
                    label="Document title"
                    name="title"
                    value={document.title}
                    maxLength={200}
                    autoFocus={!document.id}
                    required
                    onChange={(event) => update("title", event.target.value)}
                  />
                  <SelectField
                    label="Category"
                    name="category"
                    value={document.category}
                    onChange={(event) => update("category", event.target.value)}
                  >
                    {CATEGORIES.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
                  </SelectField>
                </div>

                <TextAreaField
                  label="Content"
                  hint="Markdown or plain text"
                  name="content"
                  className="min-h-[440px] font-mono text-base leading-7 sm:text-sm"
                  value={document.content}
                  maxLength={100000}
                  required
                  placeholder="# About our business\n\nExplain what the business does and what agents should know."
                  onChange={(event) => update("content", event.target.value)}
                />

                <label className="flex min-h-12 cursor-pointer items-center gap-3 rounded-xl bg-muted p-3 transition-colors hover:bg-accent">
                  <input
                    className="size-5 accent-primary sm:size-4"
                    id="include-in-agent-context"
                    name="include_in_agent_context"
                    type="checkbox"
                    checked={document.include_in_agent_context}
                    onChange={(event) => update("include_in_agent_context", event.target.checked)}
                  />
                  <span className="min-w-0">
                    <span className="font-semibold text-foreground">Use as an active agent instruction</span>
                    <span className="text-base text-muted-foreground sm:text-sm"> Agents receive and should follow this content when they load workspace context.</span>
                  </span>
                </label>

                <footer className="flex flex-wrap items-center justify-between gap-4 border-t border-neutral-100 pt-4">
                  <p className="text-base tabular-nums text-muted-foreground sm:text-sm">
                    {document.id ? `Revision ${document.revision} · Updated ${dateLabel(document.updated_at)}` : "Unsaved document"}
                  </p>
                  <div className="flex items-center gap-3">
                    {saved && <span className="text-base text-green-700 sm:text-sm" role="status">Saved as a new revision.</span>}
                    <Button variant="primary" type="submit" disabled={busy}>{busy ? "Saving…" : document.id ? "Save revision" : "Create document"}</Button>
                  </div>
                </footer>
              </form>
            </Panel>
          )}
        </div>
      )}
    </div>
  );
}
